import { IIconProps, IRectangle, IStyle, mergeStyles, useTheme } from '@fluentui/react';
import { IPFontIcon, IPIcon, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapExtraSmall, globalTextStylesBold } from 'globalStyles';
import {
  createContext,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getRandomId } from 'utils/string';
import {
  calloutEdgeSpace,
  calloutGapSpace,
  defaultCalloutWidth,
  iconFontSize,
  minCalloutWidth,
  taskDetailRowHeight,
} from './TaskDetailStyles';

interface ITaskDetailRowProps {
  iconName?: string;
  // replaces iconName with a shared icon that carries its own colour, for example the checklist state icons
  iconProps?: IIconProps;
  // bold part of the row, the colon is added here
  label: string;
  value: string;
  // not clickable, no hover
  disabled?: boolean;
  // show the value in the error colour, for example an overdue deadline
  danger?: boolean;
  calloutWidth?: number;
  // when set, a click runs this instead of opening a callout (the editor is a dialog of its own)
  onActivate?: () => void;
  // the existing editor shown in the callout
  children?: ReactNode;
}

interface ICalloutLayout {
  width: number;
  bounds: IRectangle;
}

// Lets an editor inside a callout close it, for example the rich text editor, whose iframe does not
// bubble keys to the page
const TaskDetailCalloutContext = createContext<(() => void) | undefined>(undefined);
export const useCloseTaskDetailCallout = () => useContext(TaskDetailCalloutContext);
// True when rendered inside a row callout; the editors then drop their icon column and fill the callout
export const useIsInTaskDetailCallout = () => useContext(TaskDetailCalloutContext) !== undefined;

// Layers opened by the editors (dropdown, calendar, recurrence dialog) are outside the callout for
// Fluent: keep it open for them. A dropdown replaces its element between mouse down and up, so its
// click targets the dialog overlay. A layer containing the row (the task dialog) is not nested.
const preventDismissInsideNestedLayer =
  (row: HTMLElement | null) =>
  (ev: Event | FocusEvent | KeyboardEvent | MouseEvent): boolean => {
    if (ev.type === 'scroll') return false;
    if (!(ev.target instanceof Element)) return false;
    const layer = ev.target.closest('.ms-Callout, .ms-Dialog, .ms-Overlay, .ms-Layer');
    //TinyMCE renders its menus and dialogs directly under body
    if (ev.target.closest('.tox-tinymce-aux, .tox-dialog-wrap')) return true;

    return layer !== null && !layer.contains(row);
  };

// To Do style row: icon + "Label: value" on one line; click opens the existing editor in a callout
export const TaskDetailRow = (props: ITaskDetailRowProps) => {
  const theme = useTheme();
  const [target, setTarget] = useState<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [layout, setLayout] = useState<ICalloutLayout>();
  const calloutClass = useRef(`isoplanner-task-detail-callout-${getRandomId()}`).current;

  // The callout lives in a layer on the body, so it is sized here: as wide as the row (the panel),
  // inside the dialog
  const openCallout = () => {
    if (!target) return;
    const rowRect = target.getBoundingClientRect();
    const dialogRect = (target.closest('.ms-Modal-scrollableContent') ?? document.body).getBoundingClientRect();
    const width = Math.min(
      props.calloutWidth ?? defaultCalloutWidth,
      Math.max(minCalloutWidth, rowRect.width),
      dialogRect.width - 2 * calloutEdgeSpace,
    );
    setLayout({
      width,
      bounds: {
        left: dialogRect.left + calloutEdgeSpace,
        right: dialogRect.right - calloutEdgeSpace,
        top: 0,
        bottom: window.innerHeight,
        width: dialogRect.width - 2 * calloutEdgeSpace,
        height: window.innerHeight,
      },
    });
    setIsOpen(true);
  };

  // Escape closes the callout, wherever the focus is. Not when the key belongs to an editor opened
  // from inside it (an open dropdown, a date picker popup, the recurrence dialog): that one closes
  // first.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (ev: globalThis.KeyboardEvent) => {
      if (ev.key !== 'Escape' || !(ev.target instanceof Element)) return;
      const ownLayer = ev.target.closest('.ms-Callout');
      if (ownLayer && !ownLayer.classList.contains(calloutClass)) return;
      if (ev.target.closest('.tox-tinymce-aux, .tox-dialog-wrap, .ms-Dialog, [aria-expanded="true"]')) return;
      ev.stopPropagation();
      setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, calloutClass]);

  const rowStyle: IStyle = {
    boxSizing: 'border-box',
    width: '100%',
    height: taskDetailRowHeight,
    padding: `${theme.spacing.s2} ${theme.spacing.s1}`,
    border: 'none',
    background: 'transparent',
    borderRadius: theme.effects.roundedCorner2,
    font: 'inherit',
    textAlign: 'left',
    color: theme.palette.neutralPrimary,
  };
  const buttonClass = mergeStyles(rowStyle, {
    cursor: 'pointer',
    selectors: {
      ':hover': { background: theme.palette.neutralLighter },
      ':focus-visible': { outline: `1px solid ${theme.palette.black}` },
    },
  });

  const content = (
    <IPStack
      horizontal
      verticalAlign="center"
      tokens={globalStackTokensGapExtraSmall}
      styles={{ root: { height: '100%' } }}
    >
      <IPStack.Item shrink={false}>
        {props.iconProps ? (
          <IPIcon {...props.iconProps} />
        ) : (
          <IPFontIcon
            iconName={props.iconName}
            style={{ ...iconFontSize, marginTop: 0, color: theme.palette.neutralSecondary }}
          />
        )}
      </IPStack.Item>
      <IPStack.Item styles={{ root: { minWidth: 0 } }}>
        <span
          title={`${props.label}: ${props.value}`}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: props.danger ? theme.semanticColors.errorText : undefined,
          }}
        >
          <IPText as="span" styles={globalTextStylesBold}>{`${props.label}:`}</IPText>
          {` ${props.value}`}
        </span>
      </IPStack.Item>
    </IPStack>
  );

  //
  // Main render
  //
  if (props.disabled) {
    return <div className={mergeStyles(rowStyle)}>{content}</div>;
  }

  return (
    <>
      <button
        type="button"
        ref={setTarget}
        className={buttonClass}
        onClick={() => (props.onActivate ? props.onActivate() : isOpen ? setIsOpen(false) : openCallout())}
      >
        {content}
      </button>
      {isOpen && target && layout && (
        <IPCallout
          target={target}
          className={calloutClass}
          bounds={layout.bounds}
          directionalHint={IPDirectionalHint.bottomRightEdge}
          isBeakVisible={false}
          gapSpace={calloutGapSpace}
          setInitialFocus
          onDismiss={() => setIsOpen(false)}
          preventDismissOnEvent={preventDismissInsideNestedLayer(target)}
          // padding on calloutMain: the root has no background, so padding there shows the page
          // through it
          styles={{
            root: { width: layout.width, maxWidth: layout.width },
            calloutMain: { padding: theme.spacing.m, borderRadius: theme.effects.roundedCorner4 },
          }}
        >
          <TaskDetailCalloutContext.Provider value={() => setIsOpen(false)}>
            {props.children}
          </TaskDetailCalloutContext.Provider>
        </IPCallout>
      )}
    </>
  );
};
